import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { ModalContexto } from "@/shared/components/modalContexto";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { fotografarFormulario } from "@/shared/lib/fotografiaFormulario";

interface ModalProps {
  titulo: string;
  aberto: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * Passe `true` enquanto um salvamento está em andamento: ×, Esc, toque fora e "Voltar"
   * deixam de fechar a janela (evita perder o resultado/erro de uma gravação em curso).
   */
  carregando?: boolean;
  /**
   * Padrão `true`: se a pessoa mudou algo nos campos do formulário, sair (×, Esc, toque fora,
   * "Voltar") pergunta "Sair sem salvar?". Passe `false` só quando a tela já tem a própria proteção.
   */
  protegerSaida?: boolean;
  /**
   * Para escolhas feitas fora de um `<form>` (ex.: pessoas marcadas numa lista): `true` quando já
   * há algo escolhido e sair deve perguntar "Sair sem salvar?". Soma-se à comparação dos campos.
   */
  alterado?: boolean;
}

export function Modal({
  titulo,
  aberto,
  onClose,
  children,
  carregando = false,
  protegerSaida = true,
  alterado = false,
}: ModalProps) {
  const corpoRef = useRef<HTMLDivElement>(null);
  // Foto dos campos no primeiro toque/tecla/foco da pessoa (antes de ela mudar qualquer coisa); valores
  // que chegam depois do carregamento inicial não contam como "alteração".
  const baseRef = useRef<string | null>(null);
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const confirmandoRef = useRef(false);
  confirmandoRef.current = confirmandoSaida;

  useEffect(() => {
    if (!aberto) {
      baseRef.current = null;
      setConfirmandoSaida(false);
    }
  }, [aberto]);

  const solicitarFechamento = useCallback(() => {
    if (carregando) return;
    const base = baseRef.current;
    const camposMudaram = base !== null && fotografarFormulario(corpoRef.current) !== base;
    if (protegerSaida && (alterado || camposMudaram)) setConfirmandoSaida(true);
    else onClose();
  }, [carregando, onClose, protegerSaida, alterado]);

  // Esc fecha o modal (teclado), como em qualquer diálogo acessível.
  useEffect(() => {
    if (!aberto) return;
    function aoTeclar(e: KeyboardEvent) {
      // Com uma confirmação aberta por cima, o Esc é dela (ModalConfirmacao), não desta janela.
      if (e.key === "Escape" && !confirmandoRef.current && !document.getElementById("confirmacao-titulo")) {
        solicitarFechamento();
      }
    }
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, solicitarFechamento]);

  const contexto = useMemo(() => ({ solicitarFechamento }), [solicitarFechamento]);

  if (!aberto) return null;

  function marcarBase() {
    if (baseRef.current === null) baseRef.current = fotografarFormulario(corpoRef.current);
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-titulo"
        onClick={solicitarFechamento}
      >
        <div
          className="flex max-h-full w-full max-w-md flex-col rounded-lg border border-border bg-surface shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 pt-6">
            <h2 id="modal-titulo" className="text-lg font-bold text-primary">
              {titulo}
            </h2>
            <button
              onClick={solicitarFechamento}
              disabled={carregando}
              aria-label="Fechar"
              className="-m-3 flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <div
            ref={corpoRef}
            onPointerDownCapture={marcarBase}
            onKeyDownCapture={marcarBase}
            onFocusCapture={marcarBase}
            className="overflow-y-auto px-6 pb-6 pt-4"
          >
            <ModalContexto.Provider value={contexto}>{children}</ModalContexto.Provider>
          </div>
        </div>
      </div>
      <ModalSairSemSalvar
        aberto={confirmandoSaida}
        onSair={() => {
          setConfirmandoSaida(false);
          onClose();
        }}
        onContinuar={() => setConfirmandoSaida(false)}
      />
    </>
  );
}
